import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Collapse from "@mui/material/Collapse";
import Typography from "@mui/material/Typography";
import { motion } from "framer-motion";
import utils from "../../universal/utils";
import { colors } from "../../theme/colors";
import {
  LOW_CS2_HOURS_THRESHOLD,
  MIN_STEAM_ACCOUNT_AGE_MONTHS,
  STEAM_PRIVACY_INCOGNITO_STEPS,
  STEAM_PRIVACY_SETTINGS_URL,
} from "./steamSignupNotices";

const basePath = utils.getEdgeBasePath();
const MotionBox = motion(Box);

const STEAM_LOGO_PATH =
  "M12.026 0C5.535 0 .228 5.034 0 11.41l6.467 2.659a3.66 3.66 0 0 1 2.033-.628c.062 0 .124.001.185.005l2.873-4.137v-.06c0-2.498 2.044-4.531 4.555-4.531 2.51 0 4.554 2.033 4.554 4.531 0 2.498-2.044 4.531-4.555 4.531a4.58 4.58 0 0 1-.131-.002l-4.094 2.901c.001.052.004.104.004.156 0 1.999-1.638 3.625-3.65 3.625-1.768 0-3.244-1.243-3.582-2.918L.218 15.674C1.704 20.452 6.45 24 12.026 24c6.633 0 12.013-5.349 12.013-11.94 0-6.59-5.38-12.06-12.013-12.06zM7.479 18.299l-1.482-.61c.262.546.715.998 1.32 1.25 1.31.546 2.821-.067 3.371-1.367a2.55 2.55 0 0 0 .005-1.957c-.27-.642-.778-1.146-1.426-1.418-.643-.27-1.328-.26-1.937-.039l1.531.632c.97.405 1.428 1.516 1.022 2.482-.405.965-1.522 1.42-2.488 1.014l.084.013zm10.572-9.094c0-1.66-1.359-3.012-3.029-3.012-1.671 0-3.029 1.351-3.029 3.012 0 1.661 1.358 3.012 3.029 3.012 1.67 0 3.029-1.351 3.029-3.012zm-5.301-.005a2.275 2.275 0 0 1 2.281-2.262 2.275 2.275 0 0 1 2.28 2.262 2.275 2.275 0 0 1-2.28 2.262 2.275 2.275 0 0 1-2.281-2.262z";

function SteamLogo() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width="18"
      height="18"
      aria-hidden="true"
      focusable="false"
      style={{ display: "block" }}
    >
      <path d={STEAM_LOGO_PATH} fill="currentColor" />
    </svg>
  );
}

const STEAM_SETUP_PAGE_SX = {
  position: "relative",
  minHeight: "100svh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  px: { xs: 2, md: 4 },
  py: { xs: 6, md: 8 },
  overflow: "hidden",
  background: `
    radial-gradient(
      ellipse 52% 42% at 50% 44%,
      rgba(232,163,61,0.075),
      transparent 72%
    ),
    #080808
  `,
};

const STEAM_SETUP_PANEL_SX = {
  position: "relative",
  width: "100%",
  maxWidth: "46rem",
  boxSizing: "border-box",
  px: { xs: "1.75rem", sm: "2.5rem", md: "3.5rem" },
  py: { xs: "2rem", md: "2.75rem" },
  overflow: "hidden",
  borderRadius: "18px",
  border: "1px solid rgba(255,255,255,0.065)",
  background: `
    radial-gradient(
      ellipse 70% 45% at 50% 0%,
      rgba(232,163,61,0.055),
      transparent 70%
    ),
    linear-gradient(
      145deg,
      rgba(14,15,17,0.98),
      rgba(8,9,11,0.995)
    )
  `,
  boxShadow: `
    inset 0 1px 0 rgba(255,255,255,0.035),
    0 28px 80px rgba(0,0,0,0.4)
  `,
  zIndex: 1,
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "12%",
    right: "12%",
    height: "1px",
    background: `
      linear-gradient(
        90deg,
        transparent,
        rgba(232,163,61,0.18),
        rgba(246,190,91,0.65),
        rgba(232,163,61,0.18),
        transparent
      )
    `,
    boxShadow: "0 0 14px rgba(232,163,61,0.12)",
  },
};

const CONNECT_STEAM_BUTTON_SX = {
  position: "relative",
  overflow: "hidden",
  maxWidth: "100%",
  boxSizing: "border-box",
  minWidth: { xs: "100%", sm: 260 },
  height: 52,
  mt: "2rem",
  px: "1.7rem",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.7rem",
  borderRadius: "8px",
  border: "1px solid rgba(250,194,94,0.6)",
  background: "linear-gradient(180deg, #F5B94F 0%, #E69A27 100%)",
  color: "#0A0A0A",
  fontSize: "0.76rem",
  fontWeight: 800,
  letterSpacing: "0.1em",
  textTransform: "uppercase",
  boxShadow: `
    inset 0 1px 0 rgba(255,255,255,0.3),
    0 8px 24px rgba(232,163,61,0.13)
  `,
  transition: `
    transform 220ms cubic-bezier(.22,1,.36,1),
    box-shadow 220ms ease
  `,
  "&::before": {
    content: '""',
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    background: `
      linear-gradient(
        100deg,
        transparent 25%,
        rgba(255,255,255,0.16) 50%,
        transparent 75%
      )
    `,
    transform: "translateX(-120%)",
    transition: "transform 550ms cubic-bezier(.22,1,.36,1)",
  },
  "& .MuiButton-startIcon": {
    position: "relative",
    zIndex: 1,
    m: 0,
  },
  "&:hover": {
    background: "linear-gradient(180deg, #F5B94F 0%, #E69A27 100%)",
    transform: "translateY(-2px)",
    boxShadow: `
      inset 0 1px 0 rgba(255,255,255,0.35),
      0 12px 30px rgba(232,163,61,0.19)
    `,
    "&::before": {
      transform: "translateX(120%)",
    },
  },
  "&:active": {
    transform: "translateY(0)",
  },
};

const SETUP_ENTER = {
  initial: { opacity: 0, y: 18, filter: "blur(8px)" },
  animate: { opacity: 1, y: 0, filter: "blur(0px)" },
  transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] },
};

/**
 * Mandatory step for PlaySafe sign-ins: identity confirmation requires a
 * verified SteamID64 on file. Two entry points feed this page:
 *   - `steam_link=session`: an Ouro session already exists (returning user
 *     migrating off an old PSID, or a brand-new row created via the
 *     finish-link flow that still needs a retry). Use the session-based
 *     `/auth/playsafe/link-steam` route.
 *   - `steam_link=pending`: no Ouro row exists yet (brand-new PSID). Identity
 *     is carried in a signed cookie instead of a session; use
 *     `/auth/playsafe/finish-link-steam`.
 *
 * Deliberately does NOT use the general Steam SSO button/route — that flow
 * can create an unrelated Ouro account instead of linking this PlaySafe
 * identity.
 */
export default function PlaySafeConnectSteamPage() {
  const location = useLocation();
  const [detailsOpen, setDetailsOpen] = useState(false);
  const params = new URLSearchParams(location.search);
  const steamLinkMode = params.get("steam_link") === "pending" ? "pending" : "session";
  const linkPath =
    steamLinkMode === "pending"
      ? "/auth/playsafe/finish-link-steam"
      : "/auth/playsafe/link-steam";

  return (
    <Box component="main" sx={STEAM_SETUP_PAGE_SX}>
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          width: { xs: "30rem", md: "44rem" },
          height: { xs: "30rem", md: "44rem" },
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          borderRadius: "50%",
          border: "1px solid rgba(232,163,61,0.025)",
          pointerEvents: "none",
          "&::before": {
            content: '""',
            position: "absolute",
            inset: "18%",
            borderRadius: "50%",
            border: "1px solid rgba(232,163,61,0.035)",
          },
          "&::after": {
            content: '""',
            position: "absolute",
            inset: "34%",
            borderRadius: "50%",
            border: "1px solid rgba(232,163,61,0.045)",
          },
        }}
      />

      <MotionBox {...SETUP_ENTER} sx={STEAM_SETUP_PANEL_SX}>
        <MotionBox
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.42, delay: 0.08 }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "1rem",
              mb: "1.75rem",
            }}
          >
            <Typography
              sx={{
                fontSize: "0.62rem",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: colors.primary.main,
              }}
            >
              Account Setup
            </Typography>

            <Typography
              sx={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: "0.62rem",
                fontWeight: 600,
                letterSpacing: "0.1em",
                color: colors.text.muted,
              }}
            >
              02 / 02
            </Typography>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "auto 1fr auto",
              alignItems: "center",
              gap: "0.85rem",
              mb: { xs: "2.25rem", md: "2.75rem" },
            }}
          >
            <Typography
              sx={{
                fontSize: "0.64rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: "rgba(255,255,255,0.52)",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              ✓ PlaySafe ID
            </Typography>

            <Box
              aria-hidden
              sx={{
                height: "1px",
                background:
                  "linear-gradient(90deg, rgba(232,163,61,0.5), rgba(232,163,61,0.12))",
              }}
            />

            <Typography
              sx={{
                fontSize: "0.64rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                color: colors.primary.main,
                textTransform: "uppercase",
              }}
            >
              Steam
            </Typography>
          </Box>
        </MotionBox>

        <MotionBox
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.48, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          sx={{ textAlign: "center" }}
        >
          <Typography
            component="h1"
            sx={{
              maxWidth: "32rem",
              mx: "auto",
              fontFamily: "'Inter', system-ui, sans-serif",
              fontSize: { xs: "2rem", md: "2.65rem" },
              fontWeight: 700,
              letterSpacing: "-0.045em",
              lineHeight: 1.04,
              color: colors.text.primary,
              textAlign: "center",
            }}
          >
            Connect Steam
            <br />
            to continue
          </Typography>

          <Typography
            sx={{
              maxWidth: "31rem",
              mx: "auto",
              mt: "1.15rem",
              fontSize: "0.9rem",
              fontWeight: 400,
              lineHeight: 1.7,
              textAlign: "center",
              color: "rgba(255,255,255,0.54)",
            }}
          >
            {`Ouro requires a linked Steam account to confirm your identity. We do not accept alt accounts or throwaway Steam accounts. If this account has under ${LOW_CS2_HOURS_THRESHOLD} hours of CS2, or is less than ${MIN_STEAM_ACCOUNT_AGE_MONTHS} months old, the application is highly unlikely to be accepted.`}
          </Typography>

          <Box sx={{ width: "100%", display: "flex", justifyContent: "center" }}>
            <Button
              component="a"
              href={`${basePath}${linkPath}`}
              variant="contained"
              disableElevation
              startIcon={<SteamLogo />}
              sx={CONNECT_STEAM_BUTTON_SX}
            >
              <Box component="span" sx={{ position: "relative", zIndex: 1 }}>
                Connect Steam
              </Box>
            </Button>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "0.5rem",
              mt: "0.9rem",
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 4,
                height: 4,
                transform: "rotate(45deg)",
                backgroundColor: colors.primary.main,
                opacity: 0.65,
              }}
            />
            <Typography
              sx={{
                fontSize: "0.58rem",
                fontWeight: 700,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: "rgba(255,255,255,0.3)",
              }}
            >
              Required for matchmaking
            </Typography>
          </Box>

          <ButtonBase
            aria-expanded={detailsOpen}
            aria-controls="steam-requirements-details"
            onClick={() => setDetailsOpen((open) => !open)}
            sx={{
              mt: "1.65rem",
              gap: "0.45rem",
              borderRadius: "6px",
              color: "rgba(255,255,255,0.4)",
              fontSize: "0.7rem",
              fontWeight: 600,
              transition: "color 180ms ease",
              "&:hover": {
                color: "rgba(255,255,255,0.72)",
              },
              "&:focus-visible": {
                outline: "1px solid rgba(232,163,61,0.55)",
                outlineOffset: "4px",
              },
            }}
          >
            Why is this required?
            <Box
              component="span"
              aria-hidden
              sx={{
                display: "inline-block",
                color: colors.primary.main,
                transform: detailsOpen ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 200ms ease",
              }}
            >
              →
            </Box>
          </ButtonBase>

          <Collapse in={detailsOpen} timeout={260}>
            <Box
              id="steam-requirements-details"
              sx={{
                maxWidth: "31rem",
                mx: "auto",
                mt: "1.15rem",
                pt: "1.15rem",
                textAlign: "left",
                borderTop: "1px solid rgba(255,255,255,0.055)",
              }}
            >
              <Typography
                sx={{
                  mb: "0.7rem",
                  fontSize: "0.58rem",
                  fontWeight: 700,
                  letterSpacing: "0.16em",
                  textTransform: "uppercase",
                  color: colors.primary.main,
                }}
              >
                Steam verification requirements
              </Typography>

              <Typography
                sx={{
                  mb: "0.8rem",
                  fontSize: "0.76rem",
                  lineHeight: 1.6,
                  color: "rgba(255,255,255,0.5)",
                }}
              >
                Your Steam profile and CS2 playtime must be public so we can
                verify the account. After changing{" "}
                <Box
                  component="a"
                  href={STEAM_PRIVACY_SETTINGS_URL}
                  target="_blank"
                  rel="noreferrer"
                  sx={{
                    color: colors.primary.main,
                    fontWeight: 700,
                    textDecoration: "none",
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Steam privacy settings
                </Box>
                , check in an incognito window:
              </Typography>

              {STEAM_PRIVACY_INCOGNITO_STEPS.map((step, index) => (
                <Box
                  key={step}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "0.65rem",
                    mt: "0.55rem",
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 20,
                      height: 20,
                      mt: "0.05rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "5px",
                      border: "1px solid rgba(232,163,61,0.24)",
                      backgroundColor: "rgba(232,163,61,0.07)",
                      color: colors.primary.main,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: "0.58rem",
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {index + 1}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      lineHeight: 1.55,
                      color: "rgba(255,255,255,0.58)",
                    }}
                  >
                    {step}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Collapse>

          <ButtonBase
            component={Link}
            to="/"
            sx={{
              display: "flex",
              mx: "auto",
              mt: detailsOpen ? "1.65rem" : "2.2rem",
              borderRadius: "6px",
              fontSize: "0.7rem",
              fontWeight: 600,
              color: "rgba(255,255,255,0.34)",
              transition: "color 180ms ease, margin 200ms ease",
              "&:hover": {
                color: "rgba(255,255,255,0.68)",
              },
              "&:focus-visible": {
                outline: "1px solid rgba(255,255,255,0.3)",
                outlineOffset: "4px",
              },
            }}
          >
            Use another sign-in method
          </ButtonBase>
        </MotionBox>
      </MotionBox>
    </Box>
  );
}
